import type { ReactNode } from 'react';

import { Autocomplete, Chip, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { FieldRow } from './FieldRow';

export interface ChipListFieldProps {
  label: ReactNode;
  description?: ReactNode;
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  /** Optional known suggestions; the field still accepts free-text entries. */
  options?: string[];
  /**
   * Explicit accessible name, for when `label` is rich content (e.g. a channel-scope chip/action
   * rendered alongside the text) rather than a plain string the control can read directly.
   */
  ariaLabel?: string;
}

/**
 * Free-text list editor: type a value and press Enter to add a chip. Used for
 * the many `string[]` settings (required fields, restrictions, steps, statuses…).
 */
export function ChipListField({
  label,
  description,
  value,
  onChange,
  disabled = false,
  placeholder,
  options = [],
  ariaLabel,
}: ChipListFieldProps) {
  const { t } = useTranslation('settings');
  const items = value ?? [];
  const resolvedPlaceholder = placeholder ?? t('chipListField.placeholder');
  return (
    <FieldRow label={label} description={description} alignTop>
      <Autocomplete
        multiple
        freeSolo
        disabled={disabled}
        options={options}
        value={items}
        onChange={(_event, next) => onChange(next as string[])}
        renderTags={(tags, getTagProps) =>
          tags.map((tag, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return <Chip size="small" label={tag} key={key} {...tagProps} />;
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            placeholder={items.length === 0 ? resolvedPlaceholder : undefined}
            aria-label={ariaLabel ?? (typeof label === 'string' ? label : undefined)}
          />
        )}
      />
    </FieldRow>
  );
}
